import {
    absenceTypes,
    type Availability,
    dateAdd,
    type Department,
    EmployeeStatus,
    employeeStatusColor,
    employeeStatusLabel,
    getShiftsForIssue,
    isHalfDayEntry,
    type Issue,
    type RosterTab,
    type TimeEntry,
    TimeEntryType,
    timeEntryTypeColor,
    toDurationString,
    type Venue,
} from "@pentacode/core";
import { html, LitElement, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { keyed } from "lit/directives/keyed.js";
import "../../../components/avatar/avatar";
import { popover } from "../../../directives/popover";
import "../../../elements/roster-entry";
import { app } from "../../../init";
import { isSafari } from "../../../lib/util";
import { type ColWindow, renderDayWindow } from "../lib/day-window";
import type { DayData, DepartmentData, DragData, EmployeeData } from "../types";

// TimeEntry with optional stackSize for unassigned roster entries
type TimeEntryWithStack = TimeEntry & { stackSize?: number };

// Reactive props that change on a cursor move and nothing else. A visible row whose only
// changed props are these can skip re-rendering unless it owns the active cell (see shouldUpdate).
const ACTIVE_CURSOR_PROPS = new Set(["activeDate", "activeEmployee", "activeDepartment", "activeEntry"]);

// Mirrors --max-roster-day-entries: a cell taller than this scrolls, otherwise it can clip
// (no scroll container = one fewer non-fast-scrollable region per cell, ~1.2k on a large range).
const MAX_VISIBLE_DAY_ENTRIES = 3;

@customElement("ptc-roster-row")
export class RosterRow extends LitElement {
    @property({ attribute: false })
    employee?: EmployeeData;

    @property({ attribute: false })
    days: DayData[] = [];

    @property({ attribute: false })
    department!: DepartmentData;

    @property({ attribute: false })
    venue!: Venue;

    @property({ attribute: false })
    issues!: Issue[];

    @property({ type: Boolean })
    isVisible = false;

    @property({ type: Boolean })
    releaseOffscreen = false;

    // Shared horizontal window (large ranges): render only day columns [start,end) and bracket
    // them with sized spacers. Null = render every column (week views). colWidth is the px width
    // of one day column, used to size the spacers so column alignment + scroll width are exact.
    @property({ attribute: false })
    colWindow: ColWindow | null = null;

    @property({ type: Number })
    colWidth = 0;

    @property()
    activeDate!: string | null;

    @property({ attribute: false })
    activeEmployee!: number | null;

    @property({ attribute: false })
    activeDepartment!: number | null;

    @property()
    activeEntry!: string | null;

    @property({ attribute: false })
    activeTab!: RosterTab;

    @property({ attribute: false })
    draggingFromReadonlyDay = false;

    @property({ type: Boolean })
    canMoveUp: boolean;

    @property({ type: Boolean })
    canMoveDown: boolean;

    createRenderRoot() {
        return this;
    }

    private _hasRendered = false;

    shouldUpdate(changes: Map<string, unknown>) {
        // Off-screen rows: render only when they (just) become visible — unchanged behavior.
        if (!this.isVisible) {
            return changes.has("isVisible");
        }
        // Visible row: any change other than the active-cursor props affects what it shows,
        // so render (data, days, issues, isVisible, canMove…).
        let onlyActiveChanged = changes.size > 0;
        for (const key of changes.keys()) {
            if (ACTIVE_CURSOR_PROPS.has(key)) {
                continue;
            }
            onlyActiveChanged = false;
            break;
        }
        if (!onlyActiveChanged) {
            return true;
        }
        // Only the active cell moved. A row's appearance changes only if it held the active
        // cell before or holds it now — every other visible row renders identically, so skip
        // it. Turns a full ~N-visible-row re-render per keypress into ~2 rows.
        const myDepartment = this.department?.department?.id ?? null;
        const myEmployee = this.employee?.employee?.id ?? null;
        const prevDepartment = changes.has("activeDepartment")
            ? (changes.get("activeDepartment") as number | null)
            : this.activeDepartment;
        const prevEmployee = changes.has("activeEmployee")
            ? (changes.get("activeEmployee") as number | null)
            : this.activeEmployee;
        const wasActiveRow = prevDepartment === myDepartment && prevEmployee === myEmployee;
        const isActiveRow = this.activeDepartment === myDepartment && this.activeEmployee === myEmployee;
        return wasActiveRow || isActiveRow;
    }

    render() {
        // Mount a row's subtree the first time it scrolls into view, then keep it mounted
        // instead of tearing it down when it leaves the viewport. Repeatedly adding/removing
        // the whole subtree on scroll produced a stream of childList mutations that any
        // DOM-watching browser extension (password managers, Grammarly, …) re-scans on,
        // which showed up as scroll jank. Off-screen rows still skip re-renders via
        // shouldUpdate(), and the host's reserved min-height keeps scroll geometry stable.
        // Large ranges (releaseOffscreen) opt out: there are enough rows that keeping every
        // seen row mounted dominates memory, so release the subtree when it scrolls away.
        if (this.isVisible) {
            this._hasRendered = true;
        }
        const shouldRender = this.releaseOffscreen ? this.isVisible : this._hasRendered;
        return shouldRender ? this._render() : nothing;
    }

    private _render() {
        return html`
            ${this.employee ? this._renderEmployeeHeader(this.employee) : this._renderUnassignedHeader()}
            ${this._renderOvertimeBefore()}
            ${renderDayWindow(this.days, this.colWindow, this.colWidth, (day) =>
                this._renderDay(this.department, this.employee, day)
            )}
            ${this._renderOvertimeAfter()}
        `;
    }

    private _dragstart(e: DragEvent, data: DragData) {
        const el = e.target as HTMLElement;
        const imgEl = (el.shadowRoot && (el.shadowRoot.querySelector(".container") as HTMLElement)) || el;
        const dt = e.dataTransfer!;
        dt.setData("text/plain", "42");
        dt.effectAllowed = "all";
        dt.dropEffect = "move";
        data.imageOffset = { x: imgEl.offsetWidth / 2, y: imgEl.offsetHeight / 2 };
        dt.setDragImage(
            imgEl,
            data.imageOffset.x * window.devicePixelRatio,
            data.imageOffset.y * window.devicePixelRatio
        );
        this.classList.add("dragging");
        el.classList.add("dragging");
        this.dispatchEvent(new CustomEvent("begindrag", { detail: { data } }));
    }

    private _dragenter(e: DragEvent) {
        if (this._refusesMove(e)) {
            return;
        }
        e.preventDefault();
        (e.target as HTMLElement).classList.add("dragover");
    }

    private _dragover(e: DragEvent) {
        if (this._refusesMove(e)) {
            return;
        }
        e.preventDefault();
        if (!isSafari) {
            e.dataTransfer!.dropEffect = !e.altKey ? "link" : "copy";
        }
    }

    private _refusesMove(e: DragEvent) {
        return this.draggingFromReadonlyDay && !e.altKey;
    }

    private _refuseDrop(e: DragEvent) {
        e.stopPropagation();
    }

    private _dragleave(e: DragEvent) {
        (e.target as HTMLElement).classList.remove("dragover");
    }

    private _renderEmployeeHeader({ employee, timeBalance }: EmployeeData) {
        return html`
            <div
                class="employee-header horizontal start-aligning layout"
                @click=${() => this.dispatchEvent(new CustomEvent("header-clicked"))}
            >
                <div>
                    <ptc-avatar class="small" .employee=${employee}></ptc-avatar>
                </div>

                <div class="stretch layout vertical gap">
                    <div class="small employee-name ellipsis">${employee.name}</div>

                    ${employee.status === EmployeeStatus.Active
                        ? html`
                              <ptc-ui-progress
                                  .value=${timeBalance.actual}
                                  .max=${timeBalance.nominal}
                                  .format=${(n: number) => toDurationString(n, true)}
                                  thresholdLimitTolerance=${0.1}
                                  class="tiny noprint"
                              ></ptc-ui-progress>
                          `
                        : html`
                              <div
                                  class="employee-status tiny inverted pill fill-horizontally text-centering ${employeeStatusColor(
                                      employee.status
                                  )}"
                              >
                                  ${employeeStatusLabel(employee.status)}
                              </div>
                          `}
                </div>

                <div class="employee-move-buttons">
                    <button
                        @click=${(e: Event) => {
                            e.stopPropagation();
                            this.dispatchEvent(new CustomEvent("move-up"));
                        }}
                        ?disabled=${!this.canMoveUp}
                    >
                        <ptc-ui-icon name="icon.action.move-item-up"></ptc-ui-icon>
                    </button>
                    <button
                        @click=${(e: Event) => {
                            e.stopPropagation();
                            this.dispatchEvent(new CustomEvent("move-down"));
                        }}
                        ?disabled=${!this.canMoveDown}
                    >
                        <ptc-ui-icon name="icon.action.move-item-down"></ptc-ui-icon>
                    </button>
                </div>
            </div>
        `;
    }

    private _renderUnassignedHeader() {
        return html`
            <div class="half-padded employee-header horizontal start-aligning layout">
                <ptc-ui-icon name="icon.status.unassigned" class="huge"></ptc-ui-icon>
                <div class="stretch horizontally-margined">
                    <div class="bold">${this.department.department.name}</div>
                    <div>Nicht Zugewiesen</div>
                </div>
            </div>
        `;
    }

    private _renderDay(dep: DepartmentData, emp: EmployeeData | undefined, day: DayData) {
        const { types } = this.activeTab;

        const acceptsDrop = !day.blocked && !day.readonly;
        const showVacationFirst = day.entries.some((entry) => (entry.startPlanned?.getHours() || 0) > 12);

        return html`
            <div
                class=${classMap({
                    "employee-day": true,
                    scrollable: day.entries.length >= MAX_VISIBLE_DAY_ENTRIES,
                    blocked: day.blocked,
                    today: day.today,
                    active:
                        day.date === this.activeDate &&
                        day.employee === this.activeEmployee &&
                        dep.department.id === this.activeDepartment,
                    sunday: new Date(day.date).getDay() === 0,
                    holiday: !!day.holiday,
                })}
                @dragenter=${acceptsDrop ? this._dragenter : nothing}
                @dragleave=${this._dragleave}
                @dragover=${acceptsDrop ? this._dragover : this._refuseDrop}
                @drop=${(e: DragEvent) => {
                    if (acceptsDrop) {
                        e.preventDefault();
                        this.dispatchEvent(
                            new CustomEvent("drop-into-day", {
                                detail: {
                                    dragEvent: e,
                                    employee: emp?.employee || null,
                                    department: dep.department,
                                    date: day.date,
                                },
                            })
                        );
                    }
                }}
                @click=${() => {
                    if (day.readonly) {
                        return;
                    }
                    this.dispatchEvent(
                        new CustomEvent("select", {
                            detail: {
                                employee: day.employee,
                                department: dep.department.id,
                                date: day.date,
                                entry: "new",
                            },
                        })
                    );
                }}
                ?disabled=${day.readonly}
                style=${`--entries: ${day.entries.length}`}
            >
                <button
                    ?hidden=${day.blocked}
                    class="transparent add-button ${this.activeDepartment === dep.department.id &&
                    this.activeDate === day.date &&
                    this.activeEmployee === day.employee &&
                    this.activeEntry === "new"
                        ? "selected"
                        : ""}"
                    @click=${(e: Event) => {
                        e.stopPropagation();
                        this.dispatchEvent(
                            new CustomEvent("select", {
                                detail: {
                                    employee: day.employee,
                                    department: dep.department.id,
                                    date: day.date,
                                    entry: "new",
                                },
                            })
                        );
                    }}
                >
                    <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                </button>

                ${day.blocked
                    ? html`
                          <div class="fullbleed centering layout blocked-reason">
                              <div class="ellipsis stretch padded-light" title="${day.blockedReason}">
                                  ${day.blockedReason}
                              </div>
                          </div>
                      `
                    : day.availabilities.length && app.settings.rosterDisplayAvailabilities
                      ? this._renderAvailabilities(day)
                      : emp?.employee.isBirthDay(day.date)
                        ? html`
                              <div class="fullbleed centering layout faded">
                                  <ptc-ui-icon name="icon.event.birthday" class="big faded"></ptc-ui-icon>
                              </div>
                          `
                        : ""}
                ${day.absence && (!types || types.includes(day.absence.type))
                    ? this._renderAbsence(day, showVacationFirst ? -1 : 1)
                    : nothing}
                ${day.entries
                    .filter(
                        (entry) =>
                            [TimeEntryType.Free, TimeEntryType.Work].includes(entry.type) ||
                            (TimeEntryType.CompDay === entry.type && !day.absence)
                    )
                    .map((entry) =>
                        this._renderTimeEntry({
                            entry,
                            department: dep.department,
                            stackSize: "stackSize" in entry ? (entry as TimeEntryWithStack).stackSize : undefined,
                            ...day,
                        })
                    )}
            </div>
        `;
    }

    private _renderAbsence(day: DayData, order: -1 | 1) {
        if (!day.absence) return nothing;
        const entry = day.entries.find((entry) => absenceTypes.some((type) => type === entry.type));
        const isHalfDay = isHalfDayEntry(entry);

        return html`
            <div
                class=${classMap({
                    absence: true,
                    centering: true,
                    layout: true,
                    click: true,
                    "absence-start": day.absence?.start === day.date,
                    "absence-end": day.absence?.end === dateAdd(day.date, { days: 1 }),
                    "half-day": isHalfDay,
                })}
                style="--color-highlight: ${timeEntryTypeColor(entry?.type ?? day.absence.type)}; order: ${order}"
                @click=${(ev: PointerEvent) => {
                    ev.stopPropagation();
                    this.dispatchEvent(new CustomEvent("edit-absence", { detail: { absence: day.absence! } }));
                }}
            >
                ${entry
                    ? html`<ptc-ui-icon
                          name="${app.localized.timeEntryTypeIcon(entry.type)}"
                          class="big"
                      ></ptc-ui-icon>`
                    : nothing}
            </div>
        `;
    }

    private _renderTimeEntry({
        entry,
        department,
        date,
        blocked,
        readonly,
        stackSize,
        availabilities,
    }: {
        entry: TimeEntry;
        department: Department;
        date: string;
        blocked?: boolean;
        readonly?: boolean;
        stackSize?: number;
        availabilities?: Availability[];
    }) {
        const departmentId = (entry.position && entry.position.departmentId) || department.id;
        const otherDep = !!entry.position && entry.position.departmentId !== department.id;
        const allowDrag = !otherDep && !blocked;
        const issues = this.issues.filter((issue) => getShiftsForIssue(issue).some((e) => e.id === entry.id));
        const isActive = (this.activeEntry === entry.id && this.activeDepartment === department.id) || entry.preview;

        return html`
            <ptc-roster-entry
                id="entry-${entry.id}-${departmentId}"
                draggable="${allowDrag ? "true" : "false"}"
                .entry=${entry}
                .error=${!!issues.length}
                .department=${department}
                .venue=${this.venue}
                .stackSize=${this.employee ? undefined : stackSize}
                .layout=${app.settings.rosterDisplayMode}
                class="tiny ${isActive ? "selected" : ""}"
                ?readonly=${!!readonly}
                @remove=${() => this.dispatchEvent(new CustomEvent("remove-entry", { detail: entry }))}
                @dragstart=${(e: DragEvent) => this._dragstart(e, { entry, fromReadonlyDay: readonly })}
                @select=${({ detail: { field } }: CustomEvent<{ field: string }>) =>
                    this.dispatchEvent(
                        new CustomEvent("select", {
                            detail: {
                                date,
                                employee: entry.employeeId,
                                department: departmentId,
                                entry: entry.id,
                                field,
                            },
                        })
                    )}
                style="opacity: 0;"
                .animated=${true}
                .availabilities=${availabilities}
            ></ptc-roster-entry>
        `;
    }

    private _renderAvailabilities(day: DayData) {
        const avs = day.availabilities;
        return app.settings.rosterDisplayMode === "minimal"
            ? html`
                  <div class="fullbleed availabilities">
                      ${avs[0]
                          ? this._renderAvailabilityMinimal(
                                avs[0],
                                avs[0].start || avs[0].end ? "cutoff-bottomright" : ""
                            )
                          : ""}
                      ${avs[1]
                          ? this._renderAvailabilityMinimal(avs[1], avs[1].start || avs[1].end ? "cutoff-topleft" : "")
                          : ""}
                  </div>
              `
            : this._renderAvailabilitiesFull(day.availabilities);
    }

    private _renderAvailabilityMinimal(av: Availability, cutoff: "" | "cutoff-bottomright" | "cutoff-topleft") {
        return html`
            <div class="stretching">
                ${keyed(
                    av.id,
                    html`<div
                        class="fullbleed click centering layout availability-icon ${cutoff}"
                        style="--color-highlight: ${av.color}"
                        ${av.comment
                            ? popover(
                                  html`<div class="subtle smaller">
                                      <div class="bold">${av.fullLabel}</div>
                                      <p><ptc-ui-icon name="icon.object.comment"></ptc-ui-icon>${av.comment}</p>
                                  </div>`,
                                  {
                                      trigger: "hover",
                                      alignment: "bottom",
                                  }
                              )
                            : ""}
                    >
                        <div class="fullbleed click centering layout availability-icon">
                            <ptc-ui-icon name="${av.icon}"></ptc-ui-icon>
                        </div>
                    </div>`
                )}
            </div>
        `;
    }

    private _renderAvailabilitiesFull(avs: Availability[]) {
        return html`
            <div class="fullbleed vertical stretching layout availabilities">
                ${avs.map(
                    (av) => html`
                        ${keyed(
                            av.id,
                            html`<div
                                class="click stretch centering layout availability relative"
                                style="--color-highlight: ${av.color};"
                                ${av.comment
                                    ? popover(
                                          html`<div class="subtle smaller">
                                              <div class="bold">${av.fullLabel}</div>
                                              <p><ptc-ui-icon name="icon.object.comment"></ptc-ui-icon>${av.comment}</p>
                                          </div>`,
                                          {
                                              trigger: "hover",
                                              alignment: "bottom",
                                          }
                                      )
                                    : ""}
                            >
                                <div class="smaller colored-text">
                                    <ptc-ui-icon name="${av.icon}"></ptc-ui-icon> ${av.label}
                                    ${av.comment ? html`<ptc-ui-icon name="icon.object.comment"></ptc-ui-icon>` : ""}
                                </div>
                            </div>`
                        )}
                    `
                )}
            </div>
        `;
    }

    private _renderOvertimeBefore() {
        return html`
            <div class="balance-before" ?hidden=${!app.settings.rosterDisplayTimeBalances}>
                ${this.employee
                    ? html`
                          <ptc-ui-balance
                              .value=${this.employee.timeBalance.reset ?? this.employee.timeBalance.carry}
                              .parser=${(val: number) =>
                                  Math.abs(val) >= 10
                                      ? Math.round(val).toString()
                                      : toDurationString(Math.abs(val), true)}
                          ></ptc-ui-balance>
                      `
                    : html`<div><ptc-ui-icon name="icon.time-entry.adjustment"></ptc-ui-icon></div>`}
            </div>
        `;
    }

    private _renderOvertimeAfter() {
        return html`
            <div class="balance-after" ?hidden=${!app.settings.rosterDisplayTimeBalances}>
                ${this.employee
                    ? html`
                          <ptc-ui-balance
                              .value=${this.employee.timeBalance.balance}
                              .parser=${(val: number) =>
                                  Math.abs(val) >= 10
                                      ? Math.round(val).toString()
                                      : toDurationString(Math.abs(val), true)}
                          ></ptc-ui-balance>
                      `
                    : html`<div><ptc-ui-icon name="icon.time-entry.adjustment"></ptc-ui-icon></div>`}
            </div>
        `;
    }
}
